import { useState, useEffect } from 'react';
import axios from 'axios';
import { Wallet, Info, CheckCircle, Clock } from '@phosphor-icons/react';
import { Markdown } from '@/components/Markdown';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const fmtDate = (iso) => { if (!iso) return ''; const d = new Date(iso + 'T00:00:00'); return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`; };

export default function PagamentosClientePage() {
  const [data, setData] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ps, cs] = await Promise.all([
          axios.get(`${API}/api/payment-settings`).catch(() => ({ data: {} })),
          axios.get(`${API}/api/my/consultations-payment`).catch(() => ({ data: { consultations: [] } })),
        ]);
        setData(ps.data || {});
        setConsultations((cs.data && cs.data.consultations) || []);
      } catch { setData({}); } finally { setLoading(false); }
    })();
  }, []);

  const imgUrl = (p) => p ? (p.startsWith('http') ? p : `${API}/api/files/${p}`) : '';

  if (loading) return <div className="text-center py-16 text-brand-muted" data-testid="pagamentos-cliente-page">A carregar...</div>;

  const d = data || {};
  const hasPrecario = !!(d.price_list || d.price_list_image);
  const hasMbWay = !!d.mb_way_image;
  const hasMb = !!d.mb_image;
  const hasPolicy = !!(d.cancellation_policy && d.cancellation_policy.trim());
  const hasAny = hasPrecario || hasMbWay || hasMb || hasPolicy || consultations.length > 0;

  return (
    <div className="fade-in max-w-xl mx-auto" data-testid="pagamentos-cliente-page">
      <h1 className="font-heading text-2xl text-brand-text mb-1">Pagamentos</h1>
      <p className="text-xs text-brand-muted mb-5">Preçário, métodos de pagamento e política de cancelamento.</p>

      {!hasAny ? (
        <div className="bg-white border border-brand-border rounded-2xl p-8 text-center" data-testid="pagamentos-empty">
          <Wallet size={32} className="mx-auto text-brand-placeholder mb-2" weight="duotone" />
          <p className="text-xs text-brand-muted">Informação de pagamentos ainda não disponível.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {consultations.length > 0 && (
            <div className="bg-white border border-brand-border rounded-3xl p-5" data-testid="consultations-status-section">
              <h2 className="font-heading font-medium text-base text-brand-text mb-3">As minhas consultas</h2>
              <div className="space-y-2">
                {consultations.map(c => (
                  <div key={c.id} className="flex items-center justify-between gap-3 border border-brand-border rounded-2xl px-3 py-2.5" data-testid={`consultation-row-${c.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {c.is_paid ? <CheckCircle size={18} weight="duotone" className="text-brand-green flex-shrink-0" /> : <Clock size={18} weight="duotone" className="text-amber-500 flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-sm text-brand-text truncate">{fmtDate(c.date)}{c.modality ? ` · ${c.modality}` : ''}</p>
                        <p className={`text-[11px] ${c.is_paid ? 'text-brand-green' : 'text-amber-600'}`} data-testid={`consultation-status-${c.id}`}>{c.is_paid ? 'Paga' : 'Por pagar'}{c.amount ? ` · ${Number(c.amount).toFixed(2)}€` : ''}</p>
                      </div>
                    </div>
                    {!c.is_paid && c.payment_link && (
                      <a href={c.payment_link} target="_blank" rel="noreferrer" className="flex-shrink-0 bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium" data-testid={`consultation-pay-${c.id}`}>Pagar</a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasPrecario && (
            <div className="bg-white border border-brand-border rounded-3xl p-5" data-testid="precario-section">
              <h2 className="font-heading font-medium text-base text-brand-text mb-3">Preçário</h2>
              {d.price_list && <p className="text-sm text-brand-text whitespace-pre-line mb-3">{d.price_list}</p>}
              {d.price_list_image && <img src={imgUrl(d.price_list_image)} alt="Preçário" className="max-w-full rounded-xl border border-brand-border" data-testid="precario-image" />}
            </div>
          )}

          {(hasMbWay || hasMb) && (
            <div className="bg-white border border-brand-border rounded-3xl p-5" data-testid="metodos-section">
              <h2 className="font-heading font-medium text-base text-brand-text mb-3">Métodos de pagamento</h2>
              <div className="flex flex-wrap gap-4">
                {hasMbWay && (
                  <div className="text-center" data-testid="mbway-block">
                    <p className="text-xs text-brand-muted mb-1">MB Way</p>
                    <img src={imgUrl(d.mb_way_image)} alt="MB Way" className="w-40 h-40 object-contain rounded-xl border border-brand-border" />
                  </div>
                )}
                {hasMb && (
                  <div className="text-center" data-testid="mb-block">
                    <p className="text-xs text-brand-muted mb-1">Multibanco</p>
                    <img src={imgUrl(d.mb_image)} alt="Multibanco" className="w-40 h-40 object-contain rounded-xl border border-brand-border" />
                  </div>
                )}
              </div>
            </div>
          )}

          {hasPolicy && (
            <div className="bg-brand-bg/40 border border-brand-border rounded-3xl p-5" data-testid="policy-section">
              <h2 className="font-heading font-medium text-base text-brand-text mb-2 flex items-center gap-1.5"><Info size={16} /> Política de cancelamento</h2>
              <Markdown>{d.cancellation_policy}</Markdown>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
